import React, {
 useCallback, useEffect, useMemo, Suspense, lazy,
} from 'react';
import {
 BrowserRouter as Router, Navigate, Route, Routes,
} from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { BrowserView } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import MobileFooter from 'app/shared/components/MobileFooter';
import FooterNavbar from 'app/shared/components/FooterNavbar';
import PreventReload from 'app/shared/components/PreventReload';
import { useGetConfigsQuery } from 'app/services/api/fetchGateApi';
import { setConfigs, toggleAuthModal } from 'app/store/slices/authSlice';
import getBrandName from 'app/shared/utils/BrandHelper';
import { decrypt } from 'app/shared/utils/EncryptionUtility';
import { ConfigResponse } from 'app/services/api/commonTypes';
import BrowserBackNavigation from 'app/shared/components/BrowserBackNavigation';
import AppHeader from 'app/shared/components/AppHeader';
import Layout from 'app/shared/components/Layout';
import CookieHandler from 'app/shared/components/ttl_cookie_handler';
import AppWrapper from 'app/shared/components/AppWrapper';
import LogoutListener from 'app/shared/components/LogoutListener';
import ShellGuestSessionExpiredModal from 'app/shared/components/ShellGuestSessionExpiredModal';
import { getEventNamespace } from 'app/shared/utils/AzureNamespace';
import useGetPlatformInfo from 'hooks/useGetPlatformInfo';
import useIsTStore from 'hooks/useIsTStore';
import FeatureFlags from 'app/shared/constants/FeatureFlags';
import ModalLoader from 'app/shared/components/ModalLoader';
import ShellNetworkStatusGuard from 'components/ShellNetworkStatusGuard/ShellNetworkStatusGuard';
import { RootState } from './app/store';
import T_ROUTES from './routes/routePaths';

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();

const TAppRoutes = lazy(() => import('./routes/tAppRoutes'));
const OrderPage = lazy(() => import('app/features/Order/OrderPage'));
const ConfirmationPage = lazy(() => import('app/features/Confirmation/ConfirmationPage'));
const CategoryPage = lazy(() => import('app/features/Categories/CategoryPage'));
const LocationPage = lazy(() => import('app/features/Location/LocationPage'));
const LocationStorePage = lazy(() => import('app/features/Location/LocationStorePage'));
const RecentOrdersPage = lazy(() => import('app/features/RecentOrders/RecentOrdersPage'));
const PickUpSelectionsPage = lazy(() => import('app/features/PickUpSelections/PickUpSelectionsPage'));
const ResetPasswordPage = lazy(() => import('app/features/MyAccount/ResetPassword/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('app/features/MyAccount/ForgotPassword/ForgotPasswordPage'));
const SignUpPage = lazy(() => import('app/features/MyAccount/SignUpPage'));
const GiftCardsPage = lazy(() => import('app/features/GiftCards/GiftCardsPage'));
const ProfilePage = lazy(() => import('app/features/MyAccount/profile/ProfilePage'));
const MobileGiftCardTransferPage = lazy(() => import('app/features/MobileGiftCardTransfer/MobileGiftCardTransferPage'));
const UnsubscribePage = lazy(() => import('app/features/Unsubscribe/UnsubscribePage'));
const RewardsHomePage = lazy(() => import('app/features/RewardsHome/RewardsHomePage'));

type WebAppRoutesProps = {
    isTStore: boolean;
};

type ShellAppRoutesProps = {
    isToastEnabled: boolean;
};

type StoreRouteConfig = {
    path: string;
    tElement: React.ReactElement;
    webElement: React.ReactElement;
};

// TODO: some of the components like cart and menu are redirecting
// to the web routes like /category and /menu. Since shell app doesn't has the route
// couted those redirection will not work, neet to think how to make it work with the
// generic route.

function ShellAppRoutes({ isToastEnabled }: ShellAppRoutesProps) {
    return (
        <ShellNetworkStatusGuard>
            <>
                <ShellGuestSessionExpiredModal />
                <AppHeader />
                <Routes>
                    <Route path="/v1/*" element={isToastEnabled ? <TAppRoutes /> : <Navigate to="/" replace />} />
                    <Route path="*" element={<Navigate to={isToastEnabled ? T_ROUTES.CATEGORY : "/"} replace />} />
                </Routes>
            </>
        </ShellNetworkStatusGuard>
    );
}

function WebAppRoutes({ isTStore }: WebAppRoutesProps) {
    const { isShellApp } = useGetPlatformInfo();
    const storeRoutes: StoreRouteConfig[] = [
        {
            path: '/menu',
            tElement: <Navigate to={T_ROUTES.MENU} replace />,
            webElement: <OrderPage />,
        },
        {
            path: '/category',
            tElement: <Navigate to={T_ROUTES.CATEGORY} replace />,
            webElement: <CategoryPage />,
        },
        {
            path: '/confirmation/:orderNumber',
            tElement: <Navigate to={T_ROUTES.CONFIRMATION} replace />,
            webElement: <ConfirmationPage />,
        },
    ];

    return (
        <>
            <AppHeader />
            <CookieHandler />
            <LogoutListener />
            <Layout isShellMode={isShellApp}>
                <AppWrapper>
                    <Routes>
                        <Route
                            path="/v1/*"
                            element={(
                                isTStore
                                    ? <TAppRoutes />
                                    : <Navigate to="/" replace />
                            )}
                        />
                        {storeRoutes.map(({ path, tElement, webElement }) => (
                            <Route
                                key={path}
                                path={path}
                                element={isTStore ? tElement : webElement}
                            />
                        ))}
                        <Route path="/" element={<LocationPage />} />
                        <Route path="/recentorders" element={<RecentOrdersPage />} />
                        <Route path="/locations" element={<PickUpSelectionsPage />} />
                        <Route path="/rewards" element={<RewardsHomePage />} />
                        <Route path="/order/:storeNumber" element={<LocationStorePage />} />
                        <Route path="/unsubscribe" element={<UnsubscribePage />} />
                        <Route path="*" element={<LocationPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="/join" element={<SignUpPage />} />
                        <Route path="/myaccount/*" element={<ProfilePage />} />
                        <Route
                            path="/mobile/*"
                            element={<MobileGiftCardTransferPage />}
                        />
                        <Route
                            path="/passwordreset"
                            element={<ResetPasswordPage />}
                        />
                        <Route
                            path="/forgotpassword"
                            element={<ForgotPasswordPage />}
                        />
                        <Route path="/giftcards" element={<GiftCardsPage />} />
                        <Route path="/giftcard" element={<GiftCardsPage />} />
                    </Routes>
                </AppWrapper>
            </Layout>
            {!isShellApp && <MobileFooter />}
            {!isShellApp && (
                <BrowserView>
                    <FooterNavbar />
                </BrowserView>
            )}
        </>
    );
}

function App() {
    const traceId = useMemo(() => {
        const storedTraceId = localStorage.getItem('trace_id');
        if (storedTraceId) return storedTraceId;

        const newTraceId = uuidv4();
        localStorage.setItem('trace_id', newTraceId);
        return newTraceId;
    }, []);
    const { isShellApp } = useGetPlatformInfo();

    const appInsights = useMemo(() => {
        try {
            return new ApplicationInsights({
                config: {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    instrumentationKey: process.env.REACT_APP_INSIGHTS_KEY,
                    extensions: [reactPlugin],
                    extensionConfig: {
                        [reactPlugin.identifier]: { history: browserHistory },
                    },
                },
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('App Insights cannot be initialized', error);
            return null;
        }
    }, []);

    useEffect(() => {
        if (appInsights) {
            appInsights.loadAppInsights();
            appInsights.trackPageView({ name: 'Base' });
        } else {
            // eslint-disable-next-line no-console
            console.error('App Insights cannot be loaded');
        }
    }, [appInsights]);

    const dispatch = useDispatch();
    const { brandName } = getBrandName();
    const {
        storeNumber,
        storeId,
    } = useSelector((state: RootState) => state.stores);

    const {
        data: cfgResponse,
        error: cfgError,
        isLoading: isCfgLoading,
        isFetching: isCfgFetching,
    } = useGetConfigsQuery(brandName);

    const trackConfigTrace = useCallback(
        (message: string, properties?: Record<string, unknown>) => {
            try {
                appInsights?.trackEvent({
                    name: getEventNamespace('APP_LOAD', 'APP_CONFIG_LOAD'),
                    properties: {
                        message,
                        localTimeStamp: new Date().toISOString(),
                        trace_id: traceId,
                        ...properties,
                    },
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error occurred while tracking config trace:', error);
            }
        },
        [appInsights, traceId],
    );

    const configs = useMemo(() => {
        const emptyConfig: ConfigResponse = { env: {}, brandConfigs: {} };
        if (!cfgResponse) {
            return emptyConfig;
        }

        trackConfigTrace('Raw API response received');
        try {
            const decrypted = cfgResponse.value ? decrypt(cfgResponse.value) : null;
            if (!decrypted) {
                trackConfigTrace('Decryption returned null/empty.');
                return emptyConfig;
            }

            const parsedConfigs = JSON.parse(decrypted) as ConfigResponse;
            trackConfigTrace('Successfully decrypted and parsed', {
                auto_reload: parsedConfigs?.env?.AUTO_RELOAD_ENABLED,
            });
            return parsedConfigs;
        } catch (err) {
            trackConfigTrace('Decryption or JSON.parse failed', {
                error: err instanceof Error ? err.message : String(err),
            });
            return emptyConfig;
        }
    }, [cfgResponse, trackConfigTrace]);

     // Check if its a Toast store
    const isTStore = useIsTStore();
    const isToastEnabled = configs.env[FeatureFlags.TOAST] === 'true';

    useEffect(() => {
        dispatch(setConfigs(configs));
        if (
            Object.keys(configs.brandConfigs).length === 0
            && !isCfgLoading
            && !isCfgFetching
        ) {
            trackConfigTrace('Dispatching EMPTY config to store!');
        }
    }, [configs, dispatch, isCfgFetching, isCfgLoading, trackConfigTrace]);

    useEffect(() => {
        if (cfgError) {
            trackConfigTrace('RTK Query Error:', { error: cfgError });
        }
        if (isCfgLoading) {
            trackConfigTrace('Configs Initial loading started...');
        }
    }, [cfgError, isCfgLoading, trackConfigTrace]);

    useEffect(() => {
        const handlePopState = () => {
            dispatch(toggleAuthModal(false));
            BrowserBackNavigation(storeNumber, storeId);
        };

        window.onpopstate = handlePopState;
        return () => {
            window.onpopstate = null;
        };
    }, [dispatch, storeNumber, storeId]);

    if (isCfgLoading) {
        return <ModalLoader showModal={isCfgFetching} />;
    }

    return (
        <AppInsightsContext.Provider value={reactPlugin}>
            <Router>
                <Suspense fallback={<div />}>
                    {isShellApp ? (
                        <ShellAppRoutes isToastEnabled={isToastEnabled} />
                    ) : (
                        <PreventReload isTStore={isTStore}>
                            <WebAppRoutes isTStore={isTStore} />
                        </PreventReload>
                    )}
                </Suspense>
            </Router>
        </AppInsightsContext.Provider>
    );
}

export default App;
